import { RolePermissions, SeedModule } from "../types/api";
import { toast } from "react-toastify";
import { usePermissionSeedStore } from "../store/usePermissionSeedStore";

// checking for defined or empty
export const isDefined = (value: string | number | undefined) => {
  if (value == undefined || value == null || value == "") {
    return false;
  }
  return true;
};

// Delays response
export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Creates Proxy URL
export const proxyUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return process.env.REACT_APP_PROXY_URI_DEV;
  }
  return window.location.origin;
};

export const isRegexValidate = (regex: RegExp, str: string): boolean => {
  return regex.test(str);
};

// Strips any character that isn't a letter, digit, underscore, hyphen, or period —
// used to restrict folder/file naming inputs as the user types.
export const sanitizeName = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9_.-]/g, "");
};

// ------------ object related --------
export const allKeysHaveValues = (obj: object) => {
  return Object.values(obj).every(
    (value) => value !== undefined && value !== null && value !== ""
  );
};

export const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => deepEqual(a[key], b[key]));
};

export const filterObjectByKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => keys.includes(key as K))
  ) as Pick<T, K>;

// ---------- date related functions ---------
export const datesubstract = (dateStr: string, days: number) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0]; // returns in 'YYYY-MM-DD' format
};

export const daysDifferent = (dateFrom: string, dateTo: string) => {
  const fromDate = new Date(dateFrom);
  const toDate = new Date(dateTo);
  const diffTime = toDate.getTime() - fromDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// --------------------------------

export function loadSeedFromStorage(): SeedModule[] {
  return usePermissionSeedStore.getState().seed;
}

export function generateEmptyPermissions(
  seedData: SeedModule[]
): RolePermissions {
  const permissions: RolePermissions = {};
  seedData.forEach((module) => {
    module.subModules.forEach((subModule) => {
      permissions[subModule.key] = new Set();
    });
  });
  return permissions;
}

// Utility function for combining class names
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

let isLoggingOut = false;

export const handleApiError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  const status = error.response?.status;

  if (status === 401) {
    console.error("API error (401): session expired", error);

    if (!isLoggingOut) {
      isLoggingOut = true;
      localStorage.clear();
      usePermissionSeedStore.getState().clearSeed();
      toast.error("Session expired. Please login again.");
      window.location.href = "/login";
    }
    return;
  }

  if (status === 403) {
    toast.error("You do not have permission to perform this action.");
  } else if (status === 409) {
    toast.error(error.response?.data.error);
  } else {
    toast.error(fallbackMessage);
  }
  console.error("API error:", error);
};

export const handleApiMsg = (response: any, SuccessMessage = "Success") => {
  const status = response?.status;

  if (status === 200) {
    toast.success("Successfully done");
  } else if (status === 201) {
    toast.success("Created successfully.");
  } else {
    toast.info(SuccessMessage);
  }
};

export function formatDate(value: unknown): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export {
  sanitizeExportValue,
  sanitizeExportRow,
  FORMULA_TRIGGERS,
} from "./exportUtils";

